import React, { useState, useCallback } from 'react';
import _ from 'underscore';
import { v4 as uuidV4 } from 'uuid';
import { Activity } from 'botframework-directlinejs';
import { IAdsCopilotProps } from '../types';
import { IChatSuggestion, ChatSuggestionList } from '../ChatSuggestionList';

interface IUseChatSuggestionsProps {
  onReceiveActivity?: IAdsCopilotProps['onReceiveActivity'];
  isChatSuggestionsEnabled?: boolean;
  handleChatSuggestionClick?: (suggestionText: string) => void;
}

export const extractChatSuggestionsFromActivity = (activity: any) => {
  const { QuestionSuggestions } = (_.get(activity, ['value']) || {}) as any;
  return QuestionSuggestions || [];
};

function shouldUpdateChatSuggestions(activity: Activity): boolean {
  if (activity.type === 'message') {
    return true;
  }

  if ((activity.type as any) === 'messageReaction') {
    // when receiving a reaction, keep the earlier chat suggestions
    return false;
  }

  if (activity.type === 'event') {
    if (activity.name === 'pluginResults') {
      // when receiving plugin results from summarize or fluctuations plugins, keep the earlier chat suggestions
      return false;
    }

    // by default, events will set chat suggestions to empty
    return true;
  }

  return true;
}

export const useChatSuggestions = ({
  onReceiveActivity,
  handleChatSuggestionClick,
  isChatSuggestionsEnabled,
}: IUseChatSuggestionsProps) => {
  const [chatSuggestions, setChatSuggestions] = useState<IChatSuggestion[]>([]);

  const onReceiveActivityWithChatSuggestions = useCallback<NonNullable<IAdsCopilotProps['onReceiveActivity']>>((activity, currentActivities, dl) => {
    if (isChatSuggestionsEnabled) {
      let suggestions: IChatSuggestion[] = [];
      const streamingStatus = _.get(activity as any, ['value', 'StreamingStatus']);
      if (streamingStatus === 'Completed') {
        suggestions = extractChatSuggestionsFromActivity(activity);
        suggestions = suggestions.map((suggestion) => ({
          ...suggestion,
          Id: uuidV4(),
        }));
      }
      if (shouldUpdateChatSuggestions(activity)) {
        setChatSuggestions(_.first(suggestions, 3));
      }
    }
    if (onReceiveActivity) {
      return onReceiveActivity(activity, currentActivities, dl);
    }
    return currentActivities;
  }, [onReceiveActivity]);

  const onClickChatSuggestion = useCallback((chatSuggestion: IChatSuggestion) => {
    handleChatSuggestionClick?.(chatSuggestion.Text);
  }, [handleChatSuggestionClick]);

  const renderChatSuggestionList = useCallback(() => {
    if (!isChatSuggestionsEnabled) {
      return null;
    }
    if (chatSuggestions?.length > 0) {
      return (
        <ChatSuggestionList chatSuggestions={chatSuggestions} onClickChatSuggestion={onClickChatSuggestion} />
      );
    }
    return null;
  }, [chatSuggestions, isChatSuggestionsEnabled, onClickChatSuggestion]);

  return {
    onReceiveActivity: onReceiveActivityWithChatSuggestions,
    renderChatSuggestionList,
  };
};
